import React from 'react';
import { Dialog } from '@headlessui/react';
import { useClub } from '../context/ClubContext';
import { getCourtDisplayNameById } from '../utils/courtDisplay';

const DeletionSummaryModal = ({ isOpen, onClose, summary }) => {
  const { getCategoryStyle, courtData } = useClub();
  
  if (!summary) return null;

  const { successfulDeletions, failedDeletions } = summary;

  // Format date for display
  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T12:00:00Z').toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getDisplayCourtLabel = (booking) => getCourtDisplayNameById(booking?.court, courtData, 'Court');

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 transition-opacity" />
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-slate-600 hover:text-slate-600 rounded-full p-1 hover:bg-slate-100 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <Dialog.Title className="text-xl font-semibold text-slate-800 mb-6">
            Deletion Summary
          </Dialog.Title>
          
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <span className={`inline-block border ${getCategoryStyle({ 
                category: 'Recurring',
                court: (successfulDeletions[0] || failedDeletions[0])?.court || 'tennis-1'
              })} px-3 py-1.5 rounded-lg text-sm font-medium`}>
                Deletion Summary
              </span>
              <span className="text-sm text-slate-600">
                ({successfulDeletions.length} of {successfulDeletions.length + failedDeletions.length} bookings deleted)
              </span>
            </div>

            {successfulDeletions.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-green-800 mb-2">Successfully Deleted</h3>
                <div className="bg-green-50 rounded-lg p-4 space-y-3">
                  {successfulDeletions.map((booking, index) => (
                    <div key={index} className="text-xs md:text-sm lg:text-md flex items-center justify-between">
                      <div className="text-green-800">
                        <span className="font-medium">{getDisplayCourtLabel(booking)}</span>
                        <span className="mx-2">•</span>
                        {formatDate(booking.date)}
                      </div>
                      <div className="text-green-700">
                        {booking.startTime} - {booking.endTime}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {failedDeletions.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-red-800 mb-2">Could Not Delete</h3>
                <div className="bg-red-50 rounded-lg p-4 space-y-3">
                  {failedDeletions.map((booking, index) => (
                    <div key={index} className="text-xs md:text-sm lg:text-md flex items-center justify-between">
                      <div className="text-red-800">
                        <span className="font-medium">{getDisplayCourtLabel(booking)}</span>
                        <span className="mx-2">•</span>
                        {formatDate(booking.date)}
                      </div>
                      <div className="text-red-700">
                        {booking.startTime} - {booking.endTime}
                      </div>
                    </div>
                  ))}
                  <p className="text-sm text-red-700 mt-2">
                    These bookings could not be deleted because they have users who joined them. You can only delete public bookings that have no joiners. Please contact the individuals and ask them to leave the booking before you can delete it.
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default DeletionSummaryModal;
